import type { Cents, PricingConfig } from "@/types/pricing.type";
import {
  buildLocalizedAmounts,
  buildLocalizedStripePriceIds,
  getStripeDiscountIdForCatalog,
} from "@/data/localizedPricing";

/**
 * Tag an integer minor-unit literal as {@link Cents}. Every price below is in
 * cents (e.g. `cents(1599)` = $15.99) — the single internal representation.
 */
const cents = (n: number): Cents => n as Cents;

const weeklyBLocalizedAmounts = buildLocalizedAmounts("weekly_regular");
const weeklyBStripeIds = buildLocalizedStripePriceIds("weekly_regular");
const monthlyLocalizedAmounts = buildLocalizedAmounts("monthly");
const monthlyStripeIds = buildLocalizedStripePriceIds("monthly");
const expWmqTrialLocalizedAmounts = buildLocalizedAmounts("exp_wmq_trial");
const expWmqTrialStripeIds = buildLocalizedStripePriceIds("exp_wmq_trial");
const expWmqMonthlyLocalizedAmounts = buildLocalizedAmounts("exp_wmq_monthly");
const expWmqMonthlyStripeIds = buildLocalizedStripePriceIds("exp_wmq_monthly");
const expWmqQuarterlyLocalizedAmounts =
  buildLocalizedAmounts("exp_wmq_quarterly");
const expWmqQuarterlyStripeIds =
  buildLocalizedStripePriceIds("exp_wmq_quarterly");

export const PRICING_DATA: PricingConfig = {
  version: "1.3",
  lastUpdated: "2026-08-04",
  abTestEnabled: false,
  pricingSets: [
    {
      id: "default",
      name: "Default Pricing",
      weight: 100,
      prices: [
        {
          id: "weekly",
          nameKey: "pricing_plan_weekly_vip",
          upsellYearlySavingsKey: "upsell_yearly_feature_savings_weekly",
          upsellYearlyCreditNoticeKey:
            "upsell_yearly_first_year_credit_notice_weekly",
          duration: "1 week",
          durationShort: "week",
          durationDays: 7,
          originalPrice: cents(1999),
          discountedPrice: cents(999),
          originalDailyPrice: cents(285),
          dailyPrice: cents(143),
          popular: true,
          localizedAmounts: weeklyBLocalizedAmounts,
          stripe: {
            priceId:
              weeklyBStripeIds.USD ??
              import.meta.env.VITE_STRIPE_PRICE_ID_WEEKLY_B,
            discountId: getStripeDiscountIdForCatalog("weekly_regular"),
            localizedPriceIds: weeklyBStripeIds,
          },
          sortOrder: 2,
        },
        {
          id: "monthly",
          nameKey: "pricing_plan_monthly_vip",
          upsellYearlySavingsKey: "upsell_yearly_feature_savings_monthly",
          upsellYearlyCreditNoticeKey:
            "upsell_yearly_first_year_credit_notice_monthly",
          duration: "1 month",
          durationShort: "month",
          durationDays: 30,
          originalPrice: cents(3999),
          discountedPrice: cents(1999),
          originalDailyPrice: cents(129),
          dailyPrice: cents(65),
          popular: false,
          localizedAmounts: monthlyLocalizedAmounts,
          stripe: {
            priceId:
              monthlyStripeIds.USD ??
              import.meta.env.VITE_STRIPE_PRICE_ID_MONTHLY_B,
            discountId: getStripeDiscountIdForCatalog("monthly"),
            localizedPriceIds: monthlyStripeIds,
          },
          sortOrder: 1,
        },
      ],
    },
    {
      // ?flow=exp_wmq (SHO-713). Weight 0: pinned by the funnel flow, never
      // drawn.
      id: "exp_wmq",
      name: "Exp WMQ Pricing",
      weight: 0,
      prices: [
        {
          // $9.99 for the first 7 days, then the monthly plan — a trial price +
          // trial_period_days on the monthly price, not a coupon: a coupon
          // can't shorten the first period.
          id: "exp_wmq_trial",
          nameKey: "pricing_plan_1week",
          upsellYearlySavingsKey: "upsell_yearly_feature_savings_wmq_7days",
          upsellYearlyCreditNoticeKey:
            "upsell_yearly_first_year_credit_notice_wmq_7days",
          duration: "1 week",
          durationKey: "pricing_duration_wmq_7days",
          periodKey: "pricing_period_wmq_7days",
          durationShort: "week",
          durationDays: 7,
          originalPrice: cents(3999),
          discountedPrice: cents(999),
          originalDailyPrice: cents(571),
          dailyPrice: cents(142),
          popular: false,
          trialPeriodDays: 7,
          trialFor: "exp_wmq_monthly",
          localizedAmounts: expWmqTrialLocalizedAmounts,
          stripe: {
            priceId: expWmqTrialStripeIds.USD ?? "",
            discountId: null,
            localizedPriceIds: expWmqTrialStripeIds,
            trialPriceId: import.meta.env.VITE_STRIPE_EXP_WMQ_PRICE_ID_TRIAL,
          },
          sortOrder: 1,
        },
        {
          id: "exp_wmq_monthly",
          nameKey: "pricing_plan_1month",
          upsellYearlySavingsKey: "upsell_yearly_feature_savings_wmq_1month",
          // Same sentence as the default monthly ("the 1-month plan"); only the
          // savings bullet names the plan the WMQ way.
          upsellYearlyCreditNoticeKey:
            "upsell_yearly_first_year_credit_notice_monthly",
          duration: "1 month",
          durationKey: "pricing_duration_wmq_1month",
          durationShort: "month",
          durationDays: 30,
          originalPrice: cents(3999),
          discountedPrice: cents(1999),
          originalDailyPrice: cents(133),
          dailyPrice: cents(66),
          // Preselected (SHO-713: "1 month (preselected)").
          popular: true,
          localizedAmounts: expWmqMonthlyLocalizedAmounts,
          stripe: {
            priceId: expWmqMonthlyStripeIds.USD ?? "",
            discountId: getStripeDiscountIdForCatalog("exp_wmq_monthly"),
            localizedPriceIds: expWmqMonthlyStripeIds,
          },
          sortOrder: 2,
        },
        {
          id: "exp_wmq_quarterly",
          nameKey: "pricing_plan_3month",
          upsellYearlySavingsKey: "upsell_yearly_feature_savings_wmq_3months",
          upsellYearlyCreditNoticeKey:
            "upsell_yearly_first_year_credit_notice_wmq_3months",
          duration: "3 months",
          durationShort: "quarter",
          // 13 weeks: a quarter is 91.25 days, and 91 is what makes the card's
          // per-day figure $0.43, the PM's copy ($39.99 / 90 would read $0.44).
          durationDays: 91,
          originalPrice: cents(7999),
          discountedPrice: cents(3999),
          originalDailyPrice: cents(87),
          dailyPrice: cents(43),
          popular: false,
          localizedAmounts: expWmqQuarterlyLocalizedAmounts,
          stripe: {
            priceId: expWmqQuarterlyStripeIds.USD ?? "",
            discountId: getStripeDiscountIdForCatalog("exp_wmq_quarterly"),
            localizedPriceIds: expWmqQuarterlyStripeIds,
          },
          sortOrder: 3,
        },
      ],
    },
  ],
};
